import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";
import { Mail, MapPin, Send, Loader2 } from "lucide-react";

export default function Contact() {
  const { profile } = useOutletContext();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent!");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-6 py-10" data-testid="contact-page">
      <Seo title="Contact" description="Get in touch about a role, freelance project, or collaboration." path="/contact" />
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Contact</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-light tracking-tighter mt-3 leading-[1.05]">Let's talk.</h1>
            <p className="mt-5 text-lg text-muted-foreground leading-relaxed">Tell me about your role, project, or idea. I usually reply within a day.</p>
            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-3 text-sm">
                <span className="h-10 w-10 grid place-items-center rounded-full glass"><Mail className="w-4 h-4" /></span>
                <a href={`mailto:${profile?.email}`} className="hover:underline">{profile?.email || "hello@example.com"}</a>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="h-10 w-10 grid place-items-center rounded-full glass"><MapPin className="w-4 h-4" /></span>
                {profile?.location || "Remote"}
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="glass rounded-3xl p-6 sm:p-8 space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground">Name</label>
                  <input required value={form.name} onChange={set("name")} data-testid="contact-name-input" className="mt-1.5 w-full rounded-xl bg-background/60 border border-border px-4 py-3 outline-none focus:ring-2 focus:ring-ring transition-shadow" placeholder="Your name" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Email</label>
                  <input required type="email" value={form.email} onChange={set("email")} data-testid="contact-email-input" className="mt-1.5 w-full rounded-xl bg-background/60 border border-border px-4 py-3 outline-none focus:ring-2 focus:ring-ring transition-shadow" placeholder="you@email.com" />
                </div>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Subject</label>
                <input value={form.subject} onChange={set("subject")} data-testid="contact-subject-input" className="mt-1.5 w-full rounded-xl bg-background/60 border border-border px-4 py-3 outline-none focus:ring-2 focus:ring-ring transition-shadow" placeholder="What's this about?" />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Message</label>
                <textarea required rows={5} value={form.message} onChange={set("message")} data-testid="contact-message-input" className="mt-1.5 w-full rounded-xl bg-background/60 border border-border px-4 py-3 outline-none focus:ring-2 focus:ring-ring transition-shadow resize-none" placeholder="Tell me more…" />
              </div>
              <button disabled={sending} data-testid="contact-submit-button" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-medium hover:opacity-90 transition-opacity disabled:opacity-60">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? "Sending…" : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
